import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Landing } from "./pages/Landing";
import { SignUp } from "./pages/SignUp";
import { SignIn, ForgotPassword } from "./pages/Auth";
import { UserDashboard } from "./pages/UserDashboard";
import { AdminDashboard } from "./pages/AdminDashboard";
import "./App.css";

function LoadingScreen() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(59,130,246,0.16), transparent 34%), linear-gradient(180deg, #07111f 0%, #0f172a 55%, #090d18 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "min(420px, 100%)",
          background: "rgba(8,15,28,0.76)",
          border: "1px solid rgba(148,163,184,0.14)",
          borderRadius: 28,
          padding: "34px 28px",
          boxShadow: "0 24px 80px rgba(2,6,23,0.45)",
          backdropFilter: "blur(18px)",
          textAlign: "center",
        }}
      >
        <div className="hexvault-loader-mark" aria-hidden="true">
          <div className="hexvault-loader-orbit hexvault-loader-orbit-a" />
          <div className="hexvault-loader-orbit hexvault-loader-orbit-b" />
          <div className="hexvault-loader-core">
            <svg viewBox="0 0 120 120" className="hexvault-loader-svg" role="presentation">
              <defs>
                <linearGradient id="hexvault-loader-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#2563eb" />
                  <stop offset="100%" stopColor="#34d399" />
                </linearGradient>
              </defs>
              <path
                d="M60 10 L99 32 L99 78 L60 100 L21 78 L21 32 Z"
                fill="rgba(37,99,235,0.18)"
                stroke="url(#hexvault-loader-gradient)"
                strokeWidth="6"
                strokeLinejoin="round"
              />
              <path
                d="M60 30 L81 42 L81 67 L60 80 L39 67 L39 42 Z"
                fill="rgba(15,23,42,0.92)"
                stroke="rgba(148,163,184,0.28)"
                strokeWidth="3"
                strokeLinejoin="round"
              />
              <path
                d="M60 42 C53 42 48 47 48 54 L48 57 L45 57 C43.3 57 42 58.3 42 60 L42 70 C42 71.7 43.3 73 45 73 L75 73 C76.7 73 78 71.7 78 70 L78 60 C78 58.3 76.7 57 75 57 L72 57 L72 54 C72 47 67 42 60 42 Z M60 48 C63.3 48 66 50.7 66 54 L66 57 L54 57 L54 54 C54 50.7 56.7 48 60 48 Z"
                fill="url(#hexvault-loader-gradient)"
              />
            </svg>
          </div>
        </div>
        <div style={{ color: "#f8fafc", fontSize: 30, fontWeight: 800, letterSpacing: "-0.05em", marginBottom: 8 }}>
          HexVault
        </div>
        <div style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.7 }}>
          Warming up secure routes, vault state, and storage channels.
        </div>
      </div>
    </div>
  );
}

function AuthSyncOverlay() {
  return (
    <div className="hexvault-auth-sync-overlay">
      <div className="hexvault-auth-sync-overlay__card">
        <div className="hexvault-auth-sync-overlay__spinner" />
        <div className="hexvault-auth-sync-overlay__title">Signing you in</div>
        <div className="hexvault-auth-sync-overlay__subtitle">Securing your session and preparing your vault.</div>
      </div>
    </div>
  );
}

function AppContent() {
  const [page, setPage] = useState("landing");
  const { isAuthenticated, loading, syncing, user, logout, error } = useAuth();
  const isAuthPage = ["signin", "signup", "forgot"].includes(page);
  const dashboardPage = user?.role === "admin" ? "admin" : "dashboard";

  useEffect(() => {
    if (!loading && isAuthenticated) {
      setPage(dashboardPage);
    } else if (!loading && !isAuthenticated && ["dashboard", "admin", "loading"].includes(page)) {
      setPage("signin");
    }
  }, [dashboardPage, isAuthenticated, loading, page]);

  useEffect(() => {
    const handler = () => {
      logout();
      setPage("signin");
    };

    window.addEventListener("hexvault:auth-expired", handler);
    return () => window.removeEventListener("hexvault:auth-expired", handler);
  }, [logout]);

  const handleLogout = () => {
    logout();
    setPage("landing");
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (syncing && !isAuthenticated && !isAuthPage) {
    return <LoadingScreen />;
  }

  const resolvedPage =
    !loading && isAuthenticated && ["landing", "signin", "signup", "forgot", "loading"].includes(page)
      ? dashboardPage
      : page;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { margin: 0; padding: 0; font-family: 'Space Grotesk', system-ui; }
        input::placeholder, textarea::placeholder { color: #475569; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; }
        option { background: #1e293b; color: #e2e8f0; }
      `}</style>
      {error && ["signin", "signup", "forgot"].includes(resolvedPage) && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 120, maxWidth: 420, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.28)", color: "#fecaca", borderRadius: 14, padding: "14px 16px" }}>
          {error}
        </div>
      )}
      {resolvedPage === "landing" && <Landing onNav={setPage} isAuthenticated={isAuthenticated} user={user} />}
      {resolvedPage === "signup" && <div key="signup" className="hexvault-auth-scene"><SignUp onNav={setPage} /></div>}
      {resolvedPage === "signin" && <div key="signin" className="hexvault-auth-scene"><SignIn onNav={setPage} authSyncing={syncing} /></div>}
      {resolvedPage === "forgot" && <div key="forgot" className="hexvault-auth-scene"><ForgotPassword onNav={setPage} /></div>}
      {resolvedPage === "dashboard" && <UserDashboard onNav={setPage} user={user} onLogout={handleLogout} />}
      {resolvedPage === "admin" && <AdminDashboard onNav={setPage} user={user} onLogout={handleLogout} />}
      {syncing && !isAuthenticated && ["signin", "signup", "forgot"].includes(resolvedPage) && <AuthSyncOverlay />}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
